import axios, { AxiosError, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';
import { httpOnlySecurityService } from '../../application/iam';
import { JSONResponse } from '../../types/react-router-types';
import { getAppInsights } from '../../application/telemetry/appInsights';
import { isPublicRoute } from '../utils/routeUtils';

axios.defaults.baseURL = import.meta.env.VITE_BASE_API_URL;
axios.defaults.withCredentials = true;

let isRefreshing = false;
let refreshPromise: Promise<boolean> | null = null;
// Queue to store failed requests that need to be retried after token refresh
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (error?: any) => void;
  config: InternalAxiosRequestConfig;
}> = [];
let isLoggingOut = false; // Prevent multiple logout calls

// Track OAuth redirect time to allow grace period for cookies to be available
const OAUTH_REDIRECT_GRACE_PERIOD = 5000; // 5 seconds grace period after OAuth redirect
const OAUTH_REDIRECT_KEY = 'oauth_redirect_time';

// Helper function to check if we're in the grace period after OAuth redirect
const isInOAuthGracePeriod = (): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    const redirectTime = sessionStorage.getItem(OAUTH_REDIRECT_KEY);
    if (!redirectTime) return false;
    const timeSinceRedirect = Date.now() - parseInt(redirectTime, 10);
    return timeSinceRedirect < OAUTH_REDIRECT_GRACE_PERIOD;
  } catch {
    return false;
  }
};

// Helper function to mark OAuth redirect (call this when detecting OAuth callback)
export const markOAuthRedirect = (): void => {
  if (typeof window !== 'undefined') {
    try {
      sessionStorage.setItem(OAUTH_REDIRECT_KEY, Date.now().toString());
    } catch {
      // Ignore storage errors
    }
  }
};

// Process queued requests after token refresh
// If error is provided, reject all requests; otherwise retry them
const processQueue = (error: any = null) => {
  failedQueue.forEach(({ resolve, reject, config }) => {
    if (error) {
      reject(error);
    } else {
      // Retry the original request with the new token
      resolve(axios(config));
    }
  });
  
  failedQueue = [];
};

const refreshToken = async (): Promise<boolean> => {
  // If already refreshing, return the existing promise
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }
  
  // Start refresh process
  refreshPromise = (async () => {
    try {
      isRefreshing = true;
      // The refresh() method calls the backend refresh endpoint
      // On success, the backend returns both:
      // 1. A new access token (shorter lived)
      // 2. A new refresh token (longer lived)
      // Both are stored in httpOnly cookies and automatically updated by the security service
      await httpOnlySecurityService?.refresh();
      // After successful refresh, both tokens are now updated
      // The refresh token itself has been rotated/refreshed
      processQueue(); // Retry all queued requests
      return true;
    } catch (error) {
      processQueue(error); // Reject all queued requests with error
      const appInsights = getAppInsights();
      appInsights?.trackException({ error: error as Error, properties: { context: 'Token refresh failed' } });
      
      // Trigger logout if refresh failed (only once and not in grace period)
      if (httpOnlySecurityService && !isPublicRoute() && !isLoggingOut && !isInOAuthGracePeriod()) {
        isLoggingOut = true;
        setTimeout(() => {
          httpOnlySecurityService.logout();
        }, 100);
      }
      
      return false;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();
  
  return refreshPromise;
};

axios.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    // Skip refresh for public routes or refresh endpoint to prevent loops
    const isRefreshEndpoint = config.url?.includes('/auth/refresh');
    
    // Only refresh if token is expired and we're not on a public route
    // This prevents unnecessary refresh calls and redirect loops
    // When refresh() succeeds, it updates both access token AND refresh token
    if (
      httpOnlySecurityService && 
      httpOnlySecurityService.isTokenExpired() && 
      !isPublicRoute() && 
      !isRefreshEndpoint &&
      !isLoggingOut
    ) {
      await refreshToken();
    }
    return config;
  },
  (error) => Promise.reject(error)
);

axios.interceptors.response.use(
  (response) => {
    // On successful response, check if refresh token was rotated
    // The httpOnlySecurityService should handle refresh token updates automatically via cookies
    // But we ensure the refresh token is also refreshed after successful requests
    // This happens automatically through the request interceptor calling refresh() before each request
    
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    
    // Don't handle 401 errors for refresh endpoint itself or if already logging out
    const isRefreshEndpoint = originalRequest?.url?.includes('/auth/refresh');
    
    // Handle 401 errors - queue request and refresh token, then retry all queued requests
    // Don't retry if already retried, on refresh endpoint, logging out, or on public routes
    if (
      error?.response?.status === 401 && 
      !originalRequest._retry && 
      !isRefreshEndpoint && 
      !isLoggingOut &&
      !isPublicRoute() &&
      originalRequest
    ) {
      // Mark request as retried to prevent infinite loops
      originalRequest._retry = true;
      
      // Queue this failed request to be retried after token refresh
      return new Promise((resolve, reject) => {
        failedQueue.push({
          resolve,
          reject,
          config: originalRequest
        });
        
        // If not already refreshing, trigger refresh
        // If already refreshing, the request is queued and will be retried when refresh completes
        if (!isRefreshing) {
          refreshToken().catch((refreshError) => {
            // Refresh error is already handled in refreshToken function
            // This catch is just to prevent unhandled promise rejection
          });
        }
      });
    } else if (error?.response?.status === 401) {
      // Already retried or on refresh endpoint - don't retry again
      // Log the error but don't trigger logout if in grace period
      const appInsights = getAppInsights();
      appInsights?.trackException({ 
        error: new Error('Authentication failed - received 401 response after retry'), 
        properties: { 
          context: 'Authentication error',
          originalRequest: originalRequest?.url
        } 
      });
      
      // Reject immediately to prevent further retries
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);



export const handleAxiosErrorOnActions = (error: unknown): JSONResponse => {
  if (axios.isAxiosError<{ message: string }>(error)) {
    return {
      success: false,
      error: error.response?.data?.message,
    };
  }
  return { success: false };
};

export const axiosActions = async (config: AxiosRequestConfig): Promise<JSONResponse> => {
  try {
    await axios(config);
    return { success: true };
  } catch (error) {
    return handleAxiosErrorOnActions(error);
  }
};

export default axios;
